import { useMemo, useState } from "react";
import { fromUnixTime, format, isToday, isYesterday } from "date-fns";
import { it, enUS } from "date-fns/locale";
import PageLayout from "components/layout/PageLayout";
import SafetyCard from "./views/SafetyCard";
import { useGetSafetyAlarmsQuery, usePutSafetyAlarmProgressMutation } from "./api/safetyApiSlice";
import { useSelector } from "react-redux";
import TopicSubscriber from "app/ws/TopicSubriber";
import { selectCompanyGuid } from "features/auth/authSlice";
import { useTranslation } from "react-i18next";
import { useWebSocket } from "app/ws/WebSocketProvider";
import WarningMessage from "components/commons/WarningMessage";

const SafetyDashboard = () => {

    const [t, i18n] = useTranslation(["global", "safety"])
    const companyGuid = useSelector(selectCompanyGuid)

    const { connectionStatus: wsStatus, client: wsClient } = useWebSocket()


    const { data: alarms = [], isFetching, isSuccess, refetch } = useGetSafetyAlarmsQuery();

    const [makeProgress, { isFetching: progressIsFetching }] = usePutSafetyAlarmProgressMutation();

    const formatGroupTitle = (epochTime) => {
        const date = fromUnixTime(epochTime);
        if (isToday(date)) return t("time.today");
        if (isYesterday(date)) return t("time.yesterday");;
        var lang = enUS
        if (i18n.language == "it") lang = it
        return format(date, "EEEE d MMMM yyyy", { locale: lang });
    };

    const groupAlarmsByDate = (alarms) => {
        return alarms.reduce((acc, alarm) => {
            const title = formatGroupTitle(alarm.epochTime);
            if (!acc[title]) acc[title] = [];
            acc[title].push(alarm);
            return acc;
        }, {});
    };

    // Filtri locali
    const [selectedType, setSelectedType] = useState("ALL");
    const [selectedStatus, setSelectedStatus] = useState("ALL");

    // Tipi e stati unici derivati dai dati
    const alarmTypes = useMemo(() => {
        const types = new Set(alarms.map((a) => a.type));
        return ["ALL", ...Array.from(types)];
    }, [alarms]);

    const alarmStatuses = useMemo(() => {
        const statuses = new Set(alarms.map((a) => a.status));
        return ["ALL", ...Array.from(statuses)];
    }, [alarms]);

    const filteredAlarms = useMemo(() => {
        return alarms.filter((alarm) => {
            const typeMatches = selectedType === "ALL" || alarm.type === selectedType;
            const statusMatches = selectedStatus === "ALL" || alarm.status === selectedStatus;
            return typeMatches && statusMatches;
        });
    }, [alarms, selectedType, selectedStatus]);

    const groupedAlarms = useMemo(() => groupAlarmsByDate(filteredAlarms), [filteredAlarms]);

    function handleOperatorAction(alarmGuid, action) {
        console.log(`Progress to ${action} for Alarm: ${alarmGuid}`)
        makeProgress({
            guid: alarmGuid,
            status: action
        })
    }

    const handleAction = (userGuid, actionType) => {
        console.log(`Required ${actionType} for ${userGuid}`)
        try {
            wsClient.publish(`company/${companyGuid}/safety/${userGuid}`, JSON.stringify(actionType))
        } catch (err) {
            console.error(`Error trying to publish mqtt command`)
        }
    }

    const clearFilters = () => {
        setSelectedType("ALL")
        setSelectedStatus("ALL")
    }

    const isAnyFilterApplied = () => {
        return selectedType != "ALL" || selectedStatus != "ALL"
    }

    let content = <div className="mt-4 mb-4">

        {/* Filtri */}
        <div className="d-flex gap-3 mb-4 align-items-end flex-wrap">
            <label>
                {t(`safety:entity.alarm.props_label.type`)}:
                <select
                    className="form-select"
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                >
                    {alarmTypes.map((type) => (
                        <option key={type} value={type}>
                            {t(`safety:entity.alarm.type.${type}`)}
                        </option>
                    ))}
                </select>
            </label>

            <label>
                {t(`safety:entity.alarm.props_label.status`)}:
                <select
                    className="form-select"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                >
                    {alarmStatuses.map((status) => (
                        <option key={status} value={status}>
                            {t(`safety:entity.alarm.status.${status}`)}
                        </option>
                    ))}
                </select>
            </label>
            {
                isAnyFilterApplied() &&
                <button className="btn btn-primary" onClick={clearFilters}><i className="fa-solid fa-xmark"></i> {t("action.clear_filter")}</button>
            }

        </div>

        {isAnyFilterApplied() && <WarningMessage message={t("helper.filter_applied_warning")} />}

        {/* Gruppi allarmi */}
        {isSuccess &&
            Object.entries(groupedAlarms).map(([groupTitle, groupAlarms]) => (
                <div key={groupTitle} className="mb-5">
                    <div className="row align-items-center">
                        <h5 className="fw-bold mb-3 col-auto">{groupTitle}</h5>
                        <hr className="col" />
                    </div>
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                        {groupAlarms.map((alarm) => (
                            <SafetyCard
                                key={alarm.guid + "_" + alarm.status}
                                alarm={alarm}
                                onAction={handleAction}
                                onProgress={handleOperatorAction}
                            />
                        ))}
                    </div>
                </div>
            ))}

        {/* Nessun risultato */}
        {isSuccess && filteredAlarms.length === 0 && (
            <div className="text-muted mt-5 text-center">{t("safety:label.no_alarm")}</div>
        )}
    </div>

    return (
        <>
            <PageLayout
                pageTitle={"Safety Dashboard"}
                isLoadingContent={isFetching || progressIsFetching}
                onPageRefresh={refetch}
                bodyContent={content}
            />
            <TopicSubscriber
                subTopic={`company/${companyGuid}/safety`}
                onMessageReceived={refetch}
            />
        </>

    );
};

export default SafetyDashboard;
